import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';

// YouTube Video IDs
const videoIds = [
  'qd_SFDFBovw',
  'mrVhuJUpet8',
  'e1lrdh1OA7A',
  'zVoRxYWi6A0',
  'hAekK0h3sRY',
  'xjf6t_K8-Bw',
  'e1lrdh1OA7A',
  '7UR1Mlh9-C4'
];

// Custom Thumbnails (lokale Bilder)
const customThumbnails = [
  '/images/thumb_1_moderne_medizin.jpg',
  '/images/thumb_2_offenes_mrt.jpg',
  '/images/thumb_3_kardiologie.jpg',
  '/images/thumb_4_vorsorge.jpg',
  '/images/thumb_5_schnelle_termine.jpg',
  '/images/thumb_6_rundgang.jpg',
  '/images/thumb_7_menschlichkeit.jpg',
  '/images/thumb_8_hausbesuche.jpg'
];

export const VideoHero = () => {
  const [currentVideo, setCurrentVideo] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [player, setPlayer] = useState(null);
  const [apiReady, setApiReady] = useState(false);
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  // YouTube IFrame API laden
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setApiReady(true);
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      setApiReady(true);
    };

    return () => {
      window.onYouTubeIframeAPIReady = null;
    };
  }, []);

  // Player initialisieren wenn API ready
  useEffect(() => {
    if (!apiReady || !playerRef.current) return;

    const newPlayer = new window.YT.Player(playerRef.current, {
      videoId: videoIds[currentVideo],
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        iv_load_policy: 3,
        loop: 0,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        playsinline: 1
      },
      events: {
        onReady: (event) => {
          setPlayer(event.target);
        },
        onStateChange: (event) => {
          // Video beendet = nächstes Video
          if (event.data === window.YT.PlayerState.ENDED) {
            goToNext();
          }
          // Playing state
          if (event.data === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
          }
          if (event.data === window.YT.PlayerState.PAUSED) {
            setIsPlaying(false);
          }
        }
      }
    });

    return () => {
      if (newPlayer && newPlayer.destroy) {
        newPlayer.destroy();
      }
    };
  }, [apiReady]);

  // Video wechseln
  useEffect(() => {
    if (player && player.loadVideoById) {
      player.loadVideoById(videoIds[currentVideo]);
      if (isPlaying) {
        player.playVideo();
      }
    }
  }, [currentVideo]);

  const goToNext = () => {
    setCurrentVideo((prev) => (prev + 1) % videoIds.length);
  };

  const goToPrev = () => {
    setCurrentVideo((prev) => (prev - 1 + videoIds.length) % videoIds.length);
  };

  const togglePlay = () => {
    if (!player) return;
    
    if (isPlaying) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!player) return;
    
    if (isMuted) {
      player.unMute();
    } else {
      player.mute();
    }
    setIsMuted(!isMuted);
  };

  // Custom Thumbnail oder YouTube Fallback
  const thumbnailUrl = customThumbnails[currentVideo] || `https://img.youtube.com/vi/${videoIds[currentVideo]}/maxresdefault.jpg`;

  return (
    <section 
      ref={containerRef}
      className="relative w-full aspect-square md:h-screen md:aspect-auto overflow-hidden bg-black"
    >
      {/* Video Container */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        {/* Thumbnail - nur anzeigen wenn Video NICHT läuft */}
        {!isPlaying && (
          <div className="absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-black">
            <img
              src={thumbnailUrl}
              alt="Video Thumbnail"
              className="h-full w-auto max-w-none md:w-full md:h-auto md:min-h-full"
            />
            {/* Dunkles Overlay */}
            <div className="absolute inset-0 bg-black/20" />
          </div>
        )}

        {/* YouTube Player Container - Fullscreen */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ 
              width: '177.78vh',
              height: '56.25vw',
              minWidth: '100%',
              minHeight: '100%'
            }}
          >
            <div 
              ref={playerRef}
              className="w-full h-full"
            />
          </div>
        </div>

        {/* Klickbare Fläche zum Stoppen */}
        {isPlaying && (
          <div 
            className="absolute inset-0 z-10 cursor-pointer"
            onClick={togglePlay}
          />
        )}

        {/* Play Button - GENAU IN DER MITTE */}
        {!isPlaying && (
          <button
            onClick={togglePlay}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(12px)',
              border: '2px solid rgba(255,255,255,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              cursor: 'pointer'
            }}
            data-testid="video-play-button"
          >
            <Play style={{ width: 40, height: 40, marginLeft: 4 }} />
          </button>
        )}
      </div>

      {/* Prev Button - Links */}
      <motion.button
        onClick={goToPrev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all"
        whileHover={{ scale: 1.1, x: -5 }}
        whileTap={{ scale: 0.95 }}
        data-testid="video-prev-button"
      >
        <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
      </motion.button>

      {/* Next Button - Rechts */}
      <motion.button
        onClick={goToNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all"
        whileHover={{ scale: 1.1, x: 5 }}
        whileTap={{ scale: 0.95 }}
        data-testid="video-next-button"
      >
        <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
      </motion.button>

      {/* Mute Button - Unten rechts */}
      {isPlaying && (
        <motion.button
          onClick={toggleMute}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute right-4 md:right-8 bottom-8 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all"
          data-testid="video-mute-button"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Volume2 className="w-5 h-5" />
          )}
        </motion.button>
      )}

      {/* Video Indicator Dots - Unten */}
      <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {videoIds.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentVideo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              currentVideo === index 
                ? 'bg-white w-6' 
                : 'bg-white/40 hover:bg-white/60'
            }`}
            data-testid={`video-dot-${index}`}
          />
        ))}
      </div>

      {/* Video Nummer Anzeige */}
      <div className="absolute top-20 md:top-28 left-4 md:left-8 z-20 text-white/60 text-sm">
        Video {currentVideo + 1} / {videoIds.length}
      </div>
    </section>
  );
};

export default VideoHero;
